import { numberUtils } from '@pukka/common/utils';
import { Button, Checkbox, Input, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import {
  isPromoItem as _isPromoItem,
  calculatePromoItemPrice,
  isFreeItem as _isFreeItem,
  getPromoName,
  canBeRefunded as _canBeRefunded,
  calculateItemPrice,
  isChecked,
  RefundItems,
  RefundItemsPromo,
  shouldShowCheckbox as _shouldShowCheckbox,
} from './refundUtils';
import {
  handleCheckboxChange,
  handleClickMinus,
  handleClickPlus,
  handleInputChange,
  ITransactionRefundState,
  TransactionRefundState,
} from './TransactionRefund.state';

interface RefundItemProps {
  readonly refundItems: RefundItems;
  readonly refundItemsPromo: RefundItemsPromo;
  readonly itemAmounts: ITransactionRefundState['itemAmounts'];
  readonly disableRefund?: (salesType: string, idx: number) => void;
}

interface RefundItemDescriptionProps {
  readonly itemName: string;
  readonly itemVariantName: string;
  readonly isPromoItem: boolean;
  readonly itemPrice: number;
  readonly isFreeItem: boolean;
  readonly promoItemPrice: number;
  readonly promoName: string;
}

function RefundItemDescription({
  itemName,
  itemVariantName,
  isPromoItem,
  itemPrice,
  isFreeItem,
  promoItemPrice,
  promoName,
}: RefundItemDescriptionProps) {
  return (
    <div>
      <Text className="text-ellipsis">
        {itemName} {itemVariantName}
      </Text>
      <Text color="neutral">
        {/* eslint-disable-next-line no-nested-ternary */}
        {!isPromoItem
          ? numberUtils.formatCurrency(itemPrice, 'Rp.')
          : isFreeItem
          ? 'FREE'
          : numberUtils.formatCurrency(promoItemPrice, 'Rp.')}
      </Text>
      {isPromoItem && <Text color="neutral">{isFreeItem ? `Reward - ${promoName}` : promoName}</Text>}
    </div>
  );
}

function RefundItem({ refundItems, refundItemsPromo, itemAmounts, disableRefund }: RefundItemProps) {
  return (
    <div className="mb-4">
      {Object.keys(refundItems).map((salesType) => {
        const itemsPerSalesType = refundItems[salesType].map((refundItem, idx) => {
          // to handle promo item
          const isPromoItem = _isPromoItem(refundItem.parentCheckoutUuid, refundItemsPromo);
          const promoItemPrice = calculatePromoItemPrice(refundItem, refundItemsPromo);
          const isFreeItem = _isFreeItem(refundItem.parentCheckoutUuid, refundItemsPromo);
          const promoName = getPromoName(refundItem, refundItemsPromo);
          const itemPrice = calculateItemPrice(refundItem);
          const canBeRefunded = _canBeRefunded(refundItem, itemPrice);
          const checked = isChecked(salesType, idx, refundItems);

          const shouldShowCheckbox = _shouldShowCheckbox(
            refundItem,
            salesType,
            idx,
            isPromoItem,
            isFreeItem,
            promoItemPrice,
            canBeRefunded,
            disableRefund,
          );

          return (
            <div className="flex justify-between py-2" key={idx}>
              <RefundItemDescription
                itemName={refundItem.itemName}
                itemVariantName={refundItem.itemVariantName}
                isPromoItem={isPromoItem}
                itemPrice={itemPrice}
                isFreeItem={isFreeItem}
                promoItemPrice={promoItemPrice}
                promoName={promoName}
              />
              <div className="flex items-center gap-4">
                <Input.Group orientation="horizontal">
                  <Button
                    variant="secondary"
                    size="s"
                    disabled={!checked || !canBeRefunded}
                    data-testid="decrease-btn"
                    onClick={() => handleClickMinus(isPromoItem, refundItem)}
                  >
                    -
                  </Button>
                  <Input
                    size="s"
                    placeholder="Qty"
                    value={itemAmounts[salesType][idx]}
                    inputClassName="text-center"
                    className="w-24"
                    disabled={!checked || !shouldShowCheckbox}
                    onChangeValue={(val) => handleInputChange(isPromoItem, refundItem, val)}
                  />
                  <Button
                    variant="secondary"
                    size="s"
                    disabled={!checked || !canBeRefunded}
                    data-testid="increase-btn"
                    onClick={() => handleClickPlus(isPromoItem, refundItem)}
                  >
                    +
                  </Button>
                </Input.Group>
                {shouldShowCheckbox && (
                  <Checkbox size="md" onChange={() => handleCheckboxChange(isPromoItem, refundItem)} checked={checked} />
                )}
              </div>
            </div>
          );
        });

        return (
          <div key={salesType}>
            <Text className="border-y py-2 text-center" weight="medium">
              {salesType}
            </Text>
            {itemsPerSalesType}
          </div>
        );
      })}
    </div>
  );
}

function RefundItemList() {
  const { refundItems, refundItemsPromo, itemAmounts, disableRefund, handleClickDeselectAll, handleClickSelectAll, useTransactionRefund } =
    TransactionRefundState;
  const { isSelectedAll } = useSnapshot(TransactionRefundState);

  useTransactionRefund();

  return (
    <div>
      <div className="flex items-center justify-between">
        <Text weight="bold">Item to Refund</Text>
        {isSelectedAll ? (
          <Button variant="nude-blue" onClick={handleClickDeselectAll}>
            Deselect All
          </Button>
        ) : (
          <Button variant="nude-blue" onClick={handleClickSelectAll}>
            Select All
          </Button>
        )}
      </div>

      <RefundItem refundItems={refundItems} refundItemsPromo={refundItemsPromo} itemAmounts={itemAmounts} disableRefund={disableRefund} />
    </div>
  );
}

export { RefundItemList, RefundItem, RefundItemDescription };
